import { z } from 'zod'
import { DOCUMENT_CLASSES, DocumentProviderIdSchema } from '../domain/document-policy'
import { OkResponseSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'
import { DOCUMENT_STEP_STATES, RegistrationNextSchema } from './registration'

/**
 * The member's identity-document step (identity-document spec §4.2-§4.4, §10.1-§10.3, §12.1 steps 1-6): the country
 * and document selector, the start of a hosted scan (the server chooses the provider), the return from the provider,
 * the name answer, "I can't use a document" and the withdrawal of the document consent. Codes, ids, labels and the
 * shortened display name only: no document field ever crosses this contract.
 */

/** Same values as the API's `document_attempts.state` (apps/api checks they stay equal at compile time). */
export const DOCUMENT_ATTEMPT_STATE_VALUES = [
  'creating',
  'pending',
  'collected',
  'final',
  'superseded',
  'revoked',
  'expired',
  'start_failed',
] as const

export const DocumentStepStateSchema = z.enum(DOCUMENT_STEP_STATES)
export type DocumentStepState = z.infer<typeof DocumentStepStateSchema>

/** Why the latest try did not give a usable document (the "try again" line); null otherwise. */
/**
 * `not_accepted` (face-first spec §5.2): the provider recognised a document outside what we accept for it (an uncounted
 * retry, bounded). `our_side`: the document was read but our side could not finish the check (the face engine, the
 * portrait, the routing changed meanwhile); never counted.
 */
export const DOCUMENT_FAILURES = ['quality', 'expired', 'no_decision', 'portrait', 'not_accepted', 'our_side'] as const
export const DocumentFailureSchema = z.enum(DOCUMENT_FAILURES)
export type DocumentFailure = z.infer<typeof DocumentFailureSchema>

export const DocumentCountrySchema = z.object({
  iso2: z.string().regex(/^[A-Z]{2}$/),
  label: z.string(),
  /** The provider the server routes this country to (the consent names it). */
  provider: DocumentProviderIdSchema,
  providerName: z.string(),
  classes: z.array(z.object({ class: z.enum(DOCUMENT_CLASSES), sides: z.union([z.literal(1), z.literal(2)]) })),
})
export type DocumentCountry = z.infer<typeof DocumentCountrySchema>

export const NameScriptSchema = z.enum(['latin', 'hebrew', 'arabic', 'other'])

export const DocumentAttemptSummarySchema = z.object({
  id: UuidSchema,
  state: z.enum(DOCUMENT_ATTEMPT_STATE_VALUES),
  /**
   * Counted tries left (3 − counted, never below 0). Counted per member across its registration history, not per
   * registration: a `registrationEpoch` bump keeps the tries already used.
   */
  attemptsLeft: z.number().int().min(0),
  /** The shortened display name to confirm (state `confirm_name` only), rendered inside `<bdi>`. */
  nameShort: z.string().nullable(),
  nameScript: NameScriptSchema.nullable(),
  failure: DocumentFailureSchema.nullable(),
  /**
   * The class the provider recognised when it differs from the one the member chose (face-first spec §5.2: the
   * selection was rewritten to it); null otherwise.
   */
  recognised: z.object({ class: z.enum(DOCUMENT_CLASSES) }).nullable(),
})
export type DocumentAttemptSummary = z.infer<typeof DocumentAttemptSummarySchema>

export const DocumentStatusSchema = z.object({
  /** The requirement applies to this registration (policy `required`, no first decision yet). */
  applies: z.boolean(),
  state: DocumentStepStateSchema,
  /** The document consent must be (re)given: never given, an older version, or another provider. */
  consentNeeded: z.boolean(),
  consentVersion: z.string(),
  /** ISR, PSE first, then by label; only countries a provider can process under the policy. */
  countries: z.array(DocumentCountrySchema),
  /** The latest document try of this registration, or null before the first. */
  attempt: DocumentAttemptSummarySchema.nullable(),
})
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>

export const DocumentStartBodySchema = z.object({
  /** The issuing country of the document (ISO 3166-1 alpha-2). */
  country: z.string().regex(/^[A-Z]{2}$/),
  class: z.enum(DOCUMENT_CLASSES),
  /** The separate, unticked document consent (spec §10.1). */
  consentDocument: z.literal(true),
  /**
   * What the ticked consent named (spec §10.1), asserted back to the server: the provider shown for the chosen
   * country (`DocumentCountry.provider`) and the consent text version shown (`DocumentStatus.consentVersion`). The
   * server compares both with its routing and its consent version before it records the consent or calls a provider;
   * a mismatch is 409 `document_consent_changed` (the page refreshes the disclosure and asks for the consent again).
   */
  consentProvider: DocumentProviderIdSchema,
  consentVersion: z.string().min(1).max(64),
})
export type DocumentStartBody = z.infer<typeof DocumentStartBodySchema>

export const DocumentStartResponseSchema = z.object({
  attemptId: UuidSchema,
  /** Same-origin path that redirects (303) once to the provider's hosted capture. */
  go: z.string(),
})
export type DocumentStartResponse = z.infer<typeof DocumentStartResponseSchema>

/** The provider's return query string: untrusted, it only triggers a server-side fetch (spec §3.3). */
export const DocumentReturnBodySchema = z.object({ ref: z.string().max(200).optional() })

export const DocumentNameBodySchema = z.object({
  attemptId: UuidSchema,
  answer: z.enum(['confirmed', 'disputed']),
})
export type DocumentNameBody = z.infer<typeof DocumentNameBodySchema>

export const DocumentNextResponseSchema = z.object({ next: RegistrationNextSchema })
export type DocumentNextResponse = z.infer<typeof DocumentNextResponseSchema>

/** The same-origin path of the one-time redirect to the hosted capture (a plain express route, not JSON). */
export const documentGoPath = (attemptId: string) => `/api/member/registration/document/attempts/${attemptId}/go`

export const documentRoutes = {
  registrationDocument: route({
    method: 'GET',
    path: '/api/member/registration/document',
    auth: 'registration',
    response: DocumentStatusSchema,
  }),
  registrationDocumentStart: route({
    method: 'POST',
    path: '/api/member/registration/document/attempts',
    auth: 'registration',
    body: DocumentStartBodySchema,
    response: DocumentStartResponseSchema,
  }),
  registrationDocumentReturn: route({
    method: 'POST',
    path: '/api/member/registration/document/return',
    auth: 'registration',
    body: DocumentReturnBodySchema,
    response: DocumentStatusSchema,
  }),
  registrationDocumentName: route({
    method: 'POST',
    path: '/api/member/registration/document/name',
    auth: 'registration',
    body: DocumentNameBodySchema,
    response: DocumentNextResponseSchema,
  }),
  registrationDocumentCantUse: route({
    method: 'POST',
    path: '/api/member/registration/document/cant-use',
    auth: 'registration',
    response: DocumentNextResponseSchema,
  }),
  /**
   * Face-first spec §4.4: settles the document step from `exempt`, `not_needed` (the policy switched off while it was
   * open) or a legacy `evidence_ready` unit; 409 `conflict` when nothing is ready to settle. Answers `password`.
   */
  registrationDocumentContinue: route({
    method: 'POST',
    path: '/api/member/registration/document/continue',
    auth: 'registration',
    response: DocumentNextResponseSchema,
  }),
  registrationDocumentWithdraw: route({
    method: 'POST',
    path: '/api/member/registration/document/withdraw',
    auth: 'registration',
    response: DocumentNextResponseSchema,
  }),
  /** After the first decision (member scope): deletes the document details, keeps the decision record and the name. */
  memberDocumentWithdraw: route({
    method: 'POST',
    path: '/api/member/document/withdraw',
    auth: 'member',
    response: OkResponseSchema,
  }),
}
